import React, {useContext, useState} from "react";
import {Box, Paragraph, Text, ThemeContext, Tip} from "grommet";
import useSWR, {mutate} from "swr";
import {useRouter} from "next/router";
import {handleErrors} from "../../../utils/errors";
import {formatLastSynced} from "../../../utils/dates";
import LinkShopify from "../../LinkShopify";
import {DialogContext} from "../../DialogProvider";
import {NotificationsContext} from "../../NotificationsProvider";
import {syncShopify, unlinkShopify} from "../../../utils/connections";
import ConnectionCard from "./ConnectionCard";
import {faSync as IconSync, faChevronDown as IconChevronDown, faCircleInfo as IconInfo} from "@fortawesome/pro-light-svg-icons";
import FontAwesomeIcon from "../../FontAwesomeIcon";
import {isListEmpty} from "../../../utils/object";
import Button from "../../Button";
import { MainButtonWithExtension, ExtensionButton } from "../../../utils/styled";
import NextAnchor from "../../NextAnchor";

const ShopifyConnectionComponent = ({
    user,
    canAddCommerceIntegration=true,
    maxCommerceIntegrations,
    onIntegrationChange,
    showConnectionDetails=true,
}) => {
    const [connectionsData, setConnectionsData] = useState();
    const [linkMode, setLinkMode] = useState(false);
    const notificationCtx = useContext(NotificationsContext);
    const dialogCtx = useContext(DialogContext);
    const router = useRouter();
    const isEnterprise = connectionsData && connectionsData.length > 0 && connectionsData[0]?.has_enterprise_plan;
    const theme = useContext(ThemeContext);

    const fetcher = (url) =>
        fetch(url).then(handleErrors).catch(notificationCtx.showNotification);

    useSWR("/api/settings/shopify", fetcher, {
        revalidateOnFocus: false,
        onSuccess: (data) => {
            setConnectionsData(data)
        }
    });

    const refetchStoreDetails = () => {
        mutate("/api/settings/shopify");
    }

    const toggleLinkMode = () => {
        setLinkMode(!linkMode)
    }

    const renderIntegrationLimitMessage = () => (
        <Box
            direction="row"
            align="center"
            gap="small"
            pad="small"
            round="xsmall"
            background="background-contrast"
            margin={{top: 'small'}}
        >
            <Tip content={`Your plan allows ${maxCommerceIntegrations} commerce ${maxCommerceIntegrations === 1 ? 'integration' : 'integrations'}.`}>
                <Box flex={false}>
                    <FontAwesomeIcon SelectedIcon={IconInfo} size="xxsmall" color="text-weak" />
                </Box>
            </Tip>
            <Text size="xsmall" color="text-weak">
                <NextAnchor hoverIndicator href="https://streamlinedpayments.com/pricing">Upgrade</NextAnchor> or disconnect an integration to connect.
            </Text>
        </Box>
    )

    const renderShopifyOauth = () => {
        if (!canAddCommerceIntegration) {
            return renderIntegrationLimitMessage();
        }
        return (
            <Box>
                <LinkShopify
                    userId={user?.id}
                    refreshPage={false}
                    vertical={true}
                    onLinked={() => {
                        onIntegrationChange?.();
                    }}
                    ctaColor="status-ok"
                    buttonWrapperProps={{ pad: {vertical: 'xsmall'} }}
                />
                {linkMode && <Box>
                    <Button event={{section: 'shopify_connection_card', element: 'cancel'}} onClick={toggleLinkMode} label="Cancel" secondary />
                </Box>}
            </Box>
        );
    }

    const buildMenuList = (connections=[]) => {
        const menuList =  [...connections?.map(connection => [
            (connections.length > 1 ? {
                label: `${connection.name}`,
                disabled: true
            } : undefined),
            {
                label: "Settings",
                onClick: () => { router.push(`/settings/connections/shopify/${connection.shop_domain}` )},
                event: {section: 'shopify_connection_card', element: 'store_setting', description: connection.shop_domain}
            },
            {
                label: (<Text color='status-critical'>Unlink Shopify store</Text>),
                onClick: () => {
                    dialogCtx.showDialog({
                        title: 'Unlink Shopify store',
                        message: <Paragraph fill margin={{vertical: 'none'}}>Are you sure you would like to unlink the <Text weight="bold">{connection.name}</Text> store from Streamlined?</Paragraph>,
                        callback: () => unlinkShopify(
                            connection.shop_domain,
                            notificationCtx.showNotification,
                            () => {
                                refetchStoreDetails();
                                onIntegrationChange?.();
                            }
                        ),
                        ctaText: "Unlink",
                        ctaType: "error"
                    });
                },
                event: {section: 'shopify_connection_card', element: 'unlink_store', description: connection.shop_domain}
            },
        ].filter(Boolean))];
        (isEnterprise && canAddCommerceIntegration) && menuList.push([{
            label: "Link another store",
            onClick: toggleLinkMode,
            event: {section: 'shopify_connection_card', element: 'link_another_store'}
        }]);
        return menuList;
    }

    const getCta = () => {
        if (connectionsData && !isListEmpty(connectionsData) && !linkMode) {
            return (
                <ShopifySyncButton
                    showConnectionDetails={showConnectionDetails}
                    showNotification={notificationCtx.showNotification}
                    onSync={refetchStoreDetails}
                    connections={connectionsData}
                />
            )
        }
        return null;
    }

    const getDetails = ({connections}) => {
        if (connectionsData && isListEmpty(connectionsData) || linkMode) {
            return [
                'Use your shop name to connect and start syncing orders automatically.',
                renderShopifyOauth()
            ]
        }
        const lastSynced = connections && connections[0]?.last_synced;
        return connections && connections.length > 0
            ? [
                ...connections.map((connection) => `${connection.shop_domain}.myshopify.com`),
                formatLastSynced(lastSynced)
            ].filter(Boolean)
            : [];
    }

    return (
        <ConnectionCard
            logoSrc={`/brand_logos/shopify_symbol${theme.dark ? '_white' : ''}.svg`}
            connectionName="Shopify"
            isLoaded={connectionsData}
            nested
            menuItems={ (connectionsData && isListEmpty(connectionsData) || linkMode) ? null : buildMenuList(connectionsData)}
            details={getDetails({connections: connectionsData})}
            cta={getCta()} />
    )
};

const ShopifySyncButton = ({ connections, buttonProps, showConnectionDetails, showNotification, onSync}) => {
    const [isSyncing, setIsSyncing] = useState(connections && connections.some(obj => obj.sync_in_progress === true));
    const theme = useContext(ThemeContext);

    const reSyncShopify = (lookbackDays = null) => {
        setIsSyncing(true);
        syncShopify(showNotification, onSync, ()=> {
            setIsSyncing(false)
        }, lookbackDays);
    };

    return (
        <Box direction="row" fill="horizontal">
            <MainButtonWithExtension
                event={{section: 'shopify_connection_card', element: 'sync_shopify'}}
                fill="horizontal"
                icon={
                    <Box animation={ isSyncing ? { type: "rotateRight", duration: 1350 } : undefined }>
                        <FontAwesomeIcon SelectedIcon={IconSync} size="xsmall"/>
                    </Box>
                }
                disabled={isSyncing}
                label={<Text color="text">{isSyncing ? "Sync in progress" : "Sync Shopify"}</Text>}
                onClick={() => reSyncShopify()}
                hideRightBorder
                {...(showConnectionDetails ? {primary: true, color: "status-ok", ctaColor: isSyncing ? 'neutral-5' : 'status-ok'} : {secondary: true})}
                {...buttonProps}
            />
            <ExtensionButton
                event={{section: 'shopify_connection_card', element: 'sync_shopify_lookback'}}
                disabled={isSyncing}
                dropProps={{ align: { top: "bottom", right: "right" } }}
                margin={{left: 'xxsmall'}}
                icon={<FontAwesomeIcon SelectedIcon={IconChevronDown} size="xxsmall" color="text" />}
                dropContent={
                    <Box pad="xsmall" border>
                        {[
                            { label: "From 1 week ago", days: 7 },
                            { label: "From 2 weeks ago", days: 14 },
                            { label: "From 1 month ago", days: 30 },
                            { label: "From 2 months ago", days: 60 },
                            { label: "From 3 months ago", days: 90 },
                        ].map(option => (
                            <Button
                                key={option.days}
                                event={{section: 'shopify_connection_card', element: 'sync_lookback', description: option.label}}
                                plain
                                hoverIndicator
                                onClick={() => reSyncShopify(option.days)}
                                label={<Text>{option.label}</Text>}
                                style={{ padding: '8px 12px', width: '100%', textAlign: 'left' }}
                            />
                        ))}
                    </Box>
                }
                {...(showConnectionDetails ? {primary: true, color: "status-ok", ctaColor: isSyncing ? 'neutral-5' : 'status-ok'} : {secondary: true})}
            />
        </Box>
    );
};

export default ShopifyConnectionComponent;
